import { Alert, Button, Radio, Space, Table, Typography } from 'antd'
import { InvoicedLine, InvoicedLineResolution, InvoicedLineResolutions } from './markUnbillableApi'
import {
  InvoicedLineChoices,
  UnchargedLineRow,
  resolveButtonLabel,
  toUnchargedRows,
  unchargedSummary,
} from './markUnbillableModel'

const { Paragraph, Title } = Typography

const resolutionOptions = [
  { label: 'Write off', value: InvoicedLineResolutions.WriteOff },
  { label: 'Bill it', value: InvoicedLineResolutions.Bill },
]

const unchargedColumns = (
  choices: InvoicedLineChoices,
  disabled: boolean,
  onChoiceChange: (lineId: string, resolution: InvoicedLineResolution) => void
) => [
  { title: 'Work area', dataIndex: 'workarea', key: 'workarea' },
  { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 70 },
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 100 },
  { title: 'Visit date', dataIndex: 'visitDate', key: 'visitDate', width: 110 },
  { title: 'Current price', dataIndex: 'amount', key: 'amount', width: 120, align: 'right' as const },
  { title: 'Invoice', dataIndex: 'invoices', key: 'invoices', width: 140 },
  {
    title: 'Choice',
    key: 'choice',
    width: 190,
    render: (_: unknown, row: UnchargedLineRow) => (
      <Radio.Group
        size="small"
        optionType="button"
        options={resolutionOptions}
        value={choices[row.lineId]}
        disabled={disabled}
        onChange={event => onChoiceChange(row.lineId, event.target.value)}
      />
    ),
  },
]

type Props = {
  lines: InvoicedLine[]
  choices: InvoicedLineChoices
  choiceCount: number
  loading: boolean
  submitting: boolean
  disabled: boolean
  onChoiceChange: (lineId: string, resolution: InvoicedLineResolution) => void
  onClearChoices: () => void
  onSubmit: () => void
}

const UnchargedLinesSection = ({
  lines,
  choices,
  choiceCount,
  loading,
  submitting,
  disabled,
  onChoiceChange,
  onClearChoices,
  onSubmit,
}: Props) => {
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Title level={5} style={{ marginBottom: 0 }}>Invoiced but not charged</Title>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Lines marked invoiced that were charged $0. Write a line off to make it unbillable, or bill it
        to release it from its invoice so it shows as unbilled work again.
      </Paragraph>
      <Alert type="info" showIcon message={unchargedSummary(lines)} />
      {lines.length > 0 && (
        <>
          <Table<UnchargedLineRow>
            size="small"
            columns={unchargedColumns(choices, submitting || disabled, onChoiceChange)}
            dataSource={toUnchargedRows(lines)}
            pagination={false}
            loading={loading}
            scroll={{ x: true }}
          />
          <Space>
            <Button type="primary" danger onClick={onSubmit} loading={submitting} disabled={choiceCount === 0 || disabled}>
              {resolveButtonLabel(choiceCount)}
            </Button>
            <Button onClick={onClearChoices} disabled={choiceCount === 0 || submitting || disabled}>
              Clear choices
            </Button>
          </Space>
        </>
      )}
    </Space>
  )
}

export default UnchargedLinesSection
